/**
 * Resolving the `division_id` deep-link param to a division the wizard can
 * select.
 *
 * The reader may arrive on a competition whose divisions have since changed,
 * or with filters that hide the one the link names — so a miss is an ordinary
 * outcome, not an error. The caller falls back to an unselected step and the
 * reader chooses for themselves.
 */

/** The fields a match needs. Structural, so the full registration `Division`
 * satisfies it without this file depending on that whole type. */
export interface PreselectableDivision {
  /** The division's SCHEDULE uid. This is what the rest of the wizard keys
   * divisions by — the session's own `division_id` field,
   * `updateSessionDivision`, and the team-list fetch all carry this same
   * value, which is why the URL param shares its name.
   * NOT the same value as the division's own `id`. */
  schedule_id?: string
}

/**
 * Find the division a `division_id` refers to, or null when it is not in the
 * list.
 *
 * Matched against `schedule_id` only, and deliberately NOT against `id`: a
 * registration division carries both, and the two are different values for the
 * same division. Matching `id` as a fallback could select a different division
 * than the listing the reader clicked.
 *
 * Resolve against the FILTERED list the step renders, not the raw one — a
 * division hidden by the active filters would be selected but invisible,
 * leaving the reader with a Continue button and nothing to explain it.
 */
export const findPreselectableDivision = <T extends PreselectableDivision>(
  divisions: T[] | null | undefined,
  divisionId: string | null | undefined
): T | null => {
  if (!divisionId || !divisions?.length) {
    return null
  }
  return (
    divisions.find((division) => division.schedule_id === divisionId) ?? null
  )
}
